import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useMemo, useRef } from "react";
import { getInitialConnectorValues } from "../../shared/connectors/getInitialConnectorValues";
import { isDraft } from "../../shared/connectors/isDraft";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import type { GrantEntry } from "../../shared/rbac/roles";
import { useConnectorSelectOnSave } from "../hooks/useConnectorSelectOnSave";
import { useQuery } from "../hooks/useQuery";
import { connectorDirectory } from "../lib/connectorDirectory";
import { playClick } from "../lib/sounds";
import { ConnectorLogoContainer } from "./ConnectorLogoContainer";
import { useRenderWithPlaceholders } from "./ConnectorModal/renderWithPlaceholders";
import {
  type ConnectedAvatar,
  ConnectorAvatars,
  type DisconnectedAvatar,
} from "./ConnectorAvatars";
import { ConnectorDropdown, Row, SectionLabel } from "./ConnectorDropdown";
import { type AppInfo, ConnectorOption, ConnectorRow } from "./ConnectorRow";
import { PressButton } from "./PressButton";
import {
  type AvailableItem,
  type ConnectedItem,
  ConnectorSelectorDropdown,
  type DisconnectedItem,
} from "./ConnectorSelectorDropdown";
import { Icon } from "./Icon";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { makeUrl, navigate, routes } from "../lib/urls";

// A connector the target references but can't currently resolve (missing or
// unreadable): rendered faded with reconnect/create options.
export type DisconnectedSlot = { id: string; appType: string; slug?: string };

type Props = {
  // Noun used in labels, e.g. "step" or "chat".
  parentNoun: string;
  canWrite: boolean;
  canCreateApp: boolean;
  defaultConnectorGrants: GrantEntry[] | undefined;
  connected: readonly AppInfo[];
  // Ids already attached to the target — excluded from the "available" list.
  excludeIds: ReadonlySet<string>;
  disconnected?: readonly DisconnectedSlot[];
  // May return a promise when associating requires navigation (branch switch)
  // that must settle before a freshly created app is opened.
  associate: (connectedAppId: string, slug?: string) => unknown;
  remove: (connectedAppId: string) => void;
  // Run before any add mutation, e.g. a chat lazily creating itself.
  beforeMutate?: () => void;
  // Run after a newly created app is associated; defaults to opening its modal.
  afterCreate?: (connectedAppId: string) => void;
  // Gap in px below the opener/avatar for the picker and right-click menus
  // (default 4); the chat pill bumps it so menus don't sit flush with it.
  menuMargin?: number;
};

export function ConnectorsBar({
  parentNoun,
  canWrite,
  canCreateApp,
  defaultConnectorGrants,
  connected,
  excludeIds,
  disconnected = [],
  associate,
  remove,
  beforeMutate,
  afterCreate,
  menuMargin = 4,
}: Props) {
  const z = useZero();
  const renderWithPlaceholders = useRenderWithPlaceholders();

  const [allApps] = useQuery(queries.connectedAppsIndex());

  const available = useMemo(
    () => allApps.filter((app) => !excludeIds.has(app.id) && !isDraft(app)),
    [allApps, excludeIds]
  );

  const resolveIdentifier = (appType: string) =>
    connectorDirectory.find(
      (a) => a.identifier.toLowerCase() === appType.toLowerCase()
    )?.identifier ?? appType;

  const add = (connectedAppId: string, slug?: string) => {
    beforeMutate?.();
    return associate(connectedAppId, slug);
  };

  const pendingSlugRef = useRef<string | undefined>(undefined);
  const associateOnSave = useConnectorSelectOnSave((connector) => {
    void associate(connector.id, pendingSlugRef.current);
  });

  const createApp = (appType: string, slug?: string) => {
    const identifier = resolveIdentifier(appType);
    const appDef = connectorDirectory.find((a) => a.identifier === identifier);
    if (!appDef || !defaultConnectorGrants) return;
    beforeMutate?.();
    const initialValues = getInitialConnectorValues(
      appDef.definition,
      renderWithPlaceholders
    );
    const newAppId = nanoid(12);
    z.mutate(
      sharedMutators.createConnectedApp({
        connectedAppId: newAppId,
        name: initialValues.name || appDef.name,
        identifier,
        urlPattern: initialValues.urlPattern,
        configuration: initialValues.configuration,
        secrets: {},
        createOAuthConnection: initialValues.createOAuthConnection,
        grants: defaultConnectorGrants,
      })
    );
    pendingSlugRef.current = slug;
    associateOnSave(newAppId);
    if (afterCreate) afterCreate(newAppId);
    else navigate(routes.modal.connector, { id: newAppId });
  };

  const connectedChips: ConnectedItem[] = connected.map((app) => ({
    ...app,
    manageTo: makeUrl(routes.modal.connector, { id: app.id }),
    onRemove: () => remove(app.id),
  }));

  const availableChips: AvailableItem[] = available.map((app) => ({
    ...app,
    onSelect: () => void add(app.id),
  }));

  const disconnectedChips: DisconnectedItem[] = disconnected.map((slot) => {
    const identifier = resolveIdentifier(slot.appType);
    return {
      id: slot.id,
      appType: identifier,
      slug: slot.slug,
      options: available.filter((a) => a.identifier === identifier),
      onSelect: (connectedAppId: string) => void add(connectedAppId, slot.slug),
      onCreateNew: () => createApp(slot.appType, slot.slug),
    };
  });

  const connectedAvatars: ConnectedAvatar[] = connected.map((app) => ({
    id: app.id,
    name: app.name,
    app,
    onRemove: () => remove(app.id),
  }));

  const disconnectedAvatars: DisconnectedAvatar[] = disconnectedChips.map(
    (item) => {
      const appDef = connectorDirectory.find(
        (a) => a.identifier === item.appType
      );
      const name = appDef?.name ?? item.appType;
      return {
        id: item.id,
        appType: item.appType,
        slug: item.slug,
        // Nothing to pick from — skip the dropdown and go straight to create.
        wrap: (opener) =>
          item.options.length === 0 ? (
            <PressButton onPress={() => item.onCreateNew()}>
              {opener}
            </PressButton>
          ) : (
            <ConnectorDropdown searchable={false} opener={opener}>
              {(_query, close) => (
                <>
                  <SectionLabel>
                    <Text variant="text-small" color="var(--text-secondary)">
                      Use an existing connector
                    </Text>
                  </SectionLabel>
                  {item.options.map((opt) => (
                    <ConnectorOption
                      key={opt.id}
                      app={opt}
                      onClick={() => item.onSelect(opt.id)}
                      close={close}
                    />
                  ))}
                  <SectionLabel>
                    <Text variant="text-small" color="var(--text-secondary)">
                      Add new
                    </Text>
                  </SectionLabel>
                  <Row
                    onMouseDown={(e) => {
                      if (e.button !== 0) return;
                      playClick();
                      item.onCreateNew();
                      close();
                    }}
                  >
                    <ConnectorRow
                      template={{ name, identifier: item.appType }}
                      name={name}
                      secondary={appDef?.tagLine}
                    />
                  </Row>
                </>
              )}
            </ConnectorDropdown>
          ),
      };
    }
  );

  const hasPile = connectedAvatars.length + disconnectedAvatars.length > 0;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <ConnectorAvatars
        connected={connectedAvatars}
        disconnected={disconnectedAvatars}
        parentNoun={parentNoun}
        menuMargin={menuMargin}
      />
      <ConnectorSelectorDropdown
        disabled={!canWrite}
        parentNoun={parentNoun}
        margin={menuMargin}
        style={{
          marginLeft: hasPile ? -12 : 0,
          zIndex: 10,
        }}
        opener={
          <Tooltip
            content={
              canWrite
                ? "Add connector"
                : "You don’t have permission to add connectors"
            }
          >
            <div
              style={canWrite ? undefined : { opacity: 0.5, cursor: "default" }}
            >
              <ConnectorLogoContainer
                size={24}
                outlineWidth={2}
                subtleHover={!hasPile}
              >
                <Icon name={hasPile ? "plus" : "api-key-custom"} size={12} />
              </ConnectorLogoContainer>
            </div>
          </Tooltip>
        }
        connected={connectedChips}
        available={availableChips}
        disconnected={disconnectedChips}
        onCreateApp={
          canCreateApp ? (identifier) => createApp(identifier) : undefined
        }
      />
    </div>
  );
}
